import { AxiosResponse } from 'axios';
import { makeAutoObservable, runInAction } from 'mobx';

import { axiosClient } from '../config/axios-client';
import { DEFAULT_SETTINGS } from '../constants';
import { NotificationSettings } from '../model/settings/NotificationSettings';
import {
  ChatSettingsOption,
  NotificationSectionKey,
  NotificationSettingsOption,
  PushNotificationOption,
  TableSettingsOption,
  UserSettings,
} from '../model/settings/UserSettings';
import { getValueByKey } from '../utils/common.utils';
import notificationStore from './NotificationStore';

const BASE_URL = '/settings';

class UserSettingsStore {
  public settings: UserSettings = DEFAULT_SETTINGS;
  public isInitUserSettingsLoading = false;

  public constructor() {
    makeAutoObservable(this);
  }

  public initUserSettings = (): void => {
    this.isInitUserSettingsLoading = true;
    axiosClient
      .get<UserSettings>(BASE_URL)
      .then(response => {
        runInAction(() => {
          this.settings = response.data;
        });
      })
      .finally(() => {
        runInAction(() => {
          this.isInitUserSettingsLoading = false;
        });
      });
  };

  public updateUserSettings = (
    settingsToUpdate: UserSettings = this.settings,
  ): Promise<AxiosResponse<UserSettings>> =>
    axiosClient
      .put<UserSettings>(BASE_URL, settingsToUpdate)
      .then(async response => {
        runInAction(() => {
          this.settings = response.data;
        });
        notificationStore.showNotification({
          severity: 'success',
          summary: 'Success',
          detail: 'Settings updated successfully',
        });
        return response;
      })
      .catch(error => {
        notificationStore.showNotification({
          severity: 'error',
          summary: 'Error',
          detail: 'Settings update failed',
        });
        return error;
      });

  public modifyNotificationSettings = (
    notificationSettingOption: NotificationSettingsOption,
    key: NotificationSectionKey,
  ): void => {
    Object.assign(
      getValueByKey<NotificationSettings>(this.settings, key),
      notificationSettingOption,
    );
  };

  public modifyChatSettings = (chatSettingOption: ChatSettingsOption): void => {
    this.settings.chat = { ...this.settings.chat, ...chatSettingOption };
  };

  public modifyTableSettings = (
    tableSettingOption: TableSettingsOption,
  ): void => {
    this.settings.table = { ...this.settings.table, ...tableSettingOption };
  };

  public modifyPushNotificationSettings = (
    pushNotificationOption: PushNotificationOption,
  ): void => {
    this.settings.pushNotifications = {
      ...this.settings.pushNotifications,
      ...pushNotificationOption,
    };
  };

  public setUserSettings = (settings: UserSettings): void => {
    this.settings = settings;
  };
}

const userSettingsStore = new UserSettingsStore();
export default userSettingsStore;
